import { isBlockPage } from 'src/types/models';
import { getWritingPlan } from 'src/configuration/writing-plans';

interface BlockLike {
  properties?: {
    plainText?: string | null;
    imageUrl?: string | null;
    // Template seed text shown in-editor; counts as "untouched" if plainText matches.
    placeholder?: string | null;
    // Show Work signals (paste + writing-pace) — see types/models/Block.ts
    pastedCharCount?: number;
    pastedWordCount?: number;
    writeTimeMs?: number;
  };
}

// Show Work: amber-flag threshold for "heavy paste activity" (words pasted).
export const HEAVY_PASTE_WORD_THRESHOLD = 10;

interface PageLike {
  grid?: { blocks?: BlockLike[] };
}

interface DocumentMeta {
  importantWords?: string[];
  heartWords?: string[];
  writingPlan?: string | null;
  hasImportantWords?: boolean;
  hasHeartWords?: boolean;
  hasSignalWords?: boolean;
}

interface DocumentLike {
  // Some documents store meta as a JSON-encoded string (legacy double-encoding
  // in the json column). Accept either shape and normalize via parseMeta().
  meta?: DocumentMeta | string | null;
  version?: {
    pages?: PageLike[];
    content?: { pages?: PageLike[] };
  };
}

export function parseMeta(meta: DocumentMeta | string | null | undefined): DocumentMeta {
  if (!meta) return {};
  if (typeof meta === 'string') {
    try {
      const parsed = JSON.parse(meta);
      // Defensive: handle double-encoded strings.
      if (typeof parsed === 'string') {
        return JSON.parse(parsed) || {};
      }
      return parsed || {};
    } catch {
      return {};
    }
  }
  return meta;
}

export interface MetricCount {
  used: number;
  total: number;
}

export type BaseMeta = DocumentMeta | string | null | undefined;

// The Review page synthesizes "Not Started" entries from the assignment's
// base document — which may have placeholder/sample plainText that would
// falsely register as student writing. Callers pass `isStarted=false` to
// force metrics to 0/total for those students.
function getDocumentBlocks(document: DocumentLike): BlockLike[] {
  const version = document.version;
  if (!version) return [];
  const pages = version.content?.pages || version.pages || [];
  return pages
    .filter((p) => isBlockPage(p as any))
    .flatMap((p) => p.grid?.blocks || []);
}

// A block is "authored" (i.e. the student actually wrote it) when its
// plainText is non-empty AND distinct from the template's placeholder seed.
// Template duplication copies the base doc's plainText forward, so newly-
// duplicated student docs would otherwise read as if pre-filled.
export function blockIsAuthored(block: BlockLike): boolean {
  const plain = (block.properties?.plainText || '').trim();
  const hasImage = Boolean(block.properties?.imageUrl);
  if (hasImage) return true;
  if (!plain) return false;
  const placeholder = (block.properties?.placeholder || '').trim();
  if (placeholder && plain.toLowerCase() === placeholder.toLowerCase()) {
    return false;
  }
  return true;
}

function authoredBlockText(block: BlockLike): string {
  if (!blockIsAuthored(block)) return '';
  return (block.properties?.plainText || '').trim();
}

function getDocumentText(document: DocumentLike): string {
  return getDocumentBlocks(document)
    .map(authoredBlockText)
    .join(' ')
    .toLowerCase();
}

// Word count that respects placeholder-as-empty semantics. Use this in the
// assignment grid; the global src/utils/documents.ts version counts all
// plainText and is correct for live-editing contexts where placeholder text
// has already been replaced by the editor.
export function authoredWordCount(document: DocumentLike): number {
  return getDocumentBlocks(document)
    .map(authoredBlockText)
    .filter(Boolean)
    .reduce(
      (count, text) => count + text.split(/\s+/).filter(Boolean).length,
      0,
    );
}

export function countBlocksUsed(
  document: DocumentLike,
  isStarted = true,
): MetricCount {
  const blocks = getDocumentBlocks(document);
  if (!blocks.length) return { used: 0, total: 0 };
  if (!isStarted) return { used: 0, total: blocks.length };
  return {
    used: blocks.filter(blockIsAuthored).length,
    total: blocks.length,
  };
}

function countWordHits(
  document: DocumentLike,
  words: string[] | undefined | null,
  isStarted: boolean,
): MetricCount {
  const list = Array.isArray(words) ? words : [];
  if (!list.length) return { used: 0, total: 0 };
  if (!isStarted) return { used: 0, total: list.length };
  const text = getDocumentText(document);
  const used = list.filter((w) => text.includes(w.toLowerCase())).length;
  return { used, total: list.length };
}

export function countHeartWordsUsed(
  document: DocumentLike,
  heartWords: string[] | undefined,
  isStarted = true,
): MetricCount {
  return countWordHits(document, heartWords, isStarted);
}

export function countImportantWordsUsed(
  document: DocumentLike,
  importantWords: string[] | undefined,
  isStarted = true,
): MetricCount {
  return countWordHits(document, importantWords, isStarted);
}

export function countSignalWordsUsed(
  document: DocumentLike,
  writingPlanId: string | null | undefined,
  isStarted = true,
): MetricCount {
  if (!writingPlanId) return { used: 0, total: 0 };
  const plan = getWritingPlan(writingPlanId);
  const signalWords = plan?.signalWords || [];
  return countWordHits(document, signalWords, isStarted);
}

// Show Work: total words pasted across every block in a document.
// Used by the assignment grid to flag heavy paste activity per student.
export function countPastedWords(document: DocumentLike): number {
  return getDocumentBlocks(document).reduce(
    (sum, b) => sum + (b.properties?.pastedWordCount || 0),
    0,
  );
}

// Show Work: total active-writing time across every block, in milliseconds.
// "Active" = typing sessions where edits arrived within 5s of each other
// (see Text/index.tsx onEditorStateChange).
export function countWriteTimeMs(document: DocumentLike): number {
  return getDocumentBlocks(document).reduce(
    (sum, b) => sum + (b.properties?.writeTimeMs || 0),
    0,
  );
}

// Format a write-time duration (ms) as a teacher-readable string.
// Examples: 0 → "—", 45000 → "<1m", 380000 → "6m 20s", 5400000 → "1h 30m"
export function formatWriteTime(ms: number): string {
  if (!ms || ms < 1000) return '—';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return '<1m';
  const totalMin = Math.floor(totalSec / 60);
  if (totalMin < 60) {
    const secs = totalSec % 60;
    return secs === 0 ? `${totalMin}m` : `${totalMin}m ${secs}s`;
  }
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
}

export interface StudentEntry {
  meta?: DocumentLike['meta'];
  version?: DocumentLike['version'];
  submission?: { status?: string } | null;
}

export interface ClassRollup {
  turnedIn: number;
  drafts: number;
  notStarted: number;
  blocks: MetricCount;
  heart: MetricCount;
  important: MetricCount;
  signal: MetricCount;
  hasHeart: boolean;
  hasImportant: boolean;
  hasSignal: boolean;
  // Show Work: paste activity rollup. `pastersCount` includes every started
  // student with any paste > 0 words; `heavyPastersCount` is the subset over
  // HEAVY_PASTE_WORD_THRESHOLD (the amber-flag tier).
  pastersCount: number;
  heavyPastersCount: number;
}

// Compute class-wide rollup. Averages are taken over students who have
// started (status !== 'new') — averaging over zeros hides who actually
// engaged. The denominator on each chip is the per-document total
// (e.g. heart-word list length); we only average the `used` numerator.
export function aggregateClassMetrics(
  entries: StudentEntry[],
  baseMeta: BaseMeta | undefined,
): ClassRollup {
  let turnedIn = 0;
  let drafts = 0;
  let notStarted = 0;

  const startedEntries: StudentEntry[] = [];
  entries.forEach((entry) => {
    const status = entry.submission?.status;
    if (status === 'turnedin') {
      turnedIn += 1;
      startedEntries.push(entry);
    } else if (status === 'new' || !status) {
      notStarted += 1;
    } else {
      drafts += 1;
      startedEntries.push(entry);
    }
  });

  const sum = (counts: MetricCount[]): MetricCount => {
    if (!counts.length) return { used: 0, total: 0 };
    const total = counts[0].total;
    const usedAvg = Math.round(
      counts.reduce((s, c) => s + c.used, 0) / counts.length,
    );
    return { used: usedAvg, total };
  };

  const base = parseMeta(baseMeta);
  const heartCounts = startedEntries.map((e) => countHeartWordsUsed(e, base.heartWords, true));
  const importantCounts = startedEntries.map((e) => countImportantWordsUsed(e, base.importantWords, true));
  const signalCounts = startedEntries.map((e) => countSignalWordsUsed(e, base.writingPlan, true));
  const blockCounts = startedEntries.map((e) => countBlocksUsed(e, true));

  // Show Work: count students who pasted anything, plus those over the
  // heavy-paste threshold. Only started students are eligible — not-started
  // students can't have pasted yet.
  let pastersCount = 0;
  let heavyPastersCount = 0;
  startedEntries.forEach((e) => {
    const pasted = countPastedWords(e);
    if (pasted > 0) pastersCount += 1;
    if (pasted > HEAVY_PASTE_WORD_THRESHOLD) heavyPastersCount += 1;
  });

  return {
    turnedIn,
    drafts,
    notStarted,
    blocks: sum(blockCounts),
    heart: sum(heartCounts),
    important: sum(importantCounts),
    signal: sum(signalCounts),
    hasHeart: Boolean(
      base.hasHeartWords && (base.heartWords?.length || 0) > 0,
    ),
    hasImportant: Boolean(
      base.hasImportantWords && (base.importantWords?.length || 0) > 0,
    ),
    hasSignal: Boolean(
      base.hasSignalWords && base.writingPlan,
    ),
    pastersCount,
    heavyPastersCount,
  };
}
